import { Button } from "@sellar-io/components";
import { PriceTierGetResponseType } from "@sellar-io/api-v2/types";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { v4 } from "uuid";
import { SpinnerLegacy } from "../../../../components";
import { Alert } from "../../../../components/alert/Alert";
import { NavigationBar } from "../../../../components/navigation/NavigationBar";
import { PageContainer } from "../../../../components/pageContainer/PageContainer";
import { PRICE_TIER, PRICE_TIER_EDIT } from "../../../../constants/routes";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { usePriceTier } from "../../../../hooks/api/v2/priceTier/usePriceTier";
import { usePriceTierUpdate } from "../../../../hooks/api/v2/priceTier/usePriceTierUpdate";
import { useParamAsNumber } from "../../../../hooks/useParamAsNumber";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../../hooks/usePriceFormat";
import { PriceTierForm, PriceTierFormValues } from "../PriceTierForm";
import { PriceTierDiscountsEdit } from "./PriceTierDiscountsEdit";
import { PriceTierOrderRulesEdit } from "./PriceTierOrderRulesEdit";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";

export const PriceTierEdit = () => {
  const navigate = useNavigate();
  const priceTierId = useParamAsNumber(PRICE_TIER_EDIT, "priceTierId");
  const priceTierQuery = usePriceTier(priceTierId);
  const remoteFeatureFlags = useRemoteFeatureFlags();

  const unbundlingRelease2aEnabled = remoteFeatureFlags.isActive(
    "unbundlingRelease2A",
  );

  return (
    <>
      <NavigationBar
        leftIcon={"CHEVRON_LEFT"}
        title={`Edit Price Tier`}
        onClickLeftIcon={() => navigate(PRICE_TIER.href({ priceTierId }))}
      />
      {priceTierQuery.isPending ? (
        <SpinnerLegacy />
      ) : priceTierQuery.isError ? (
        <div>Something went wrong...</div>
      ) : (
        <PageContainer className={"flex flex-col gap-6 p-6"}>
          <PriceTierFormEdit priceTier={priceTierQuery.data} />
          {!unbundlingRelease2aEnabled && (
            <PriceTierOrderRulesEdit
              priceTierId={priceTierQuery.data.id}
              orderRulesId={priceTierQuery.data.OrderRules?.id ?? null}
              rules={priceTierQuery.data.OrderRules?.PriceTierOrderRules ?? []}
            />
          )}
          <PriceTierDiscountsEdit
            priceTierId={priceTierQuery.data.id}
            volumeDiscountsId={priceTierQuery.data.VolumeDiscounts?.id ?? null}
            discounts={
              priceTierQuery.data.VolumeDiscounts?.PriceTierDiscounts ?? []
            }
          />
        </PageContainer>
      )}
    </>
  );
};

const formId = v4();

const PriceTierFormEdit = ({
  priceTier,
}: {
  priceTier: PriceTierGetResponseType;
}) => {
  const form = useForm<PriceTierFormValues>({
    defaultValues: {
      name: priceTier.name,
      minimumOrderPrice: formatPriceApiToInput(priceTier.minimumOrderPrice),
    },
  });

  const priceTierUpdateMutation = usePriceTierUpdate(priceTier.id);

  return (
    <div className={"flex flex-col gap-2"}>
      <PriceTierForm
        form={form}
        formId={formId}
        onSave={(data) => {
          const apiData = {
            name: data.name,
            minimumOrderPrice: formatPriceInputToApi(data.minimumOrderPrice),
          };
          console.log(apiData);
          priceTierUpdateMutation.mutate(apiData);
        }}
      />
      {priceTierUpdateMutation.isError ? (
        <Alert
          type={"error"}
          message={
            apiV2AssertError(priceTierUpdateMutation.error)?.response?.data
              .message ?? "Unknown error occurred"
          }
        ></Alert>
      ) : null}
      <Button
        loading={priceTierUpdateMutation.isPending}
        disabled={!form.formState.isDirty || !form.formState.isValid}
        form={formId}
        type={"submit"}
      >
        Save
      </Button>
    </div>
  );
};
